import {
  arrayOf,
  bool,
  node,
  oneOfType,
  object,
  shape,
  string,
  number
} from 'prop-types';
import React, { isValidElement } from 'react';
import classnames from 'classnames';
import BundleItemComponent from 'components/BundleItemComponent';
import Ad from 'components/Ad';
import PersonProfile from 'components/PersonProfile';
import Teaser from 'components/Teaser';
import TeaserCard from 'components/TeaserCard';
import TeaserRecipeCardWithAuth, {
  TeaserRecipeCard
} from 'components/TeaserRecipeCard';
import BundleSponsor from 'components/BundleSponsor';
import { SITE_NAME } from 'constants/site';
import { extrapolateExceptions, createSetBundleItemProps } from './helper';
import VARIANT_PICTURE_SPECS from './variantPictureSpecs';
import VARIANTS, { DEFAULT_VARIANT } from './variantOptions';
import DEFAULT_BUNDLE_PICTURE_SPECS from './bundlePictureSpecs';

const NON_CONTENT_TYPES = ['ad'];

const BUNDLE_COMPONENTS = {
  ad: Ad,
  teaser: Teaser,
  teaserCard: TeaserCard,
  teaserRecipeCard: TeaserRecipeCard,
  teaserRecipeCardWithAuth: TeaserRecipeCardWithAuth,
  profile: PersonProfile // Team page
};

/**
 * the base SpreadBundle. handles rendering the provided items as a bundle of
 * the specified variant.
 *
 * for the moment, our use case is rendering items of type 'teaser', so the
 * setBundleItemProps() method we create is focused on figuring out the correct
 * picture specs for each index's item. we could easily refactor to be more
 * item-type-agnostic.
 *
 */
function SpreadBundle(props) {
  const {
    data: { ads, title, description, link, sponsor },
    items,
    gtmAttrs,
    bundlePictureSpecs: baseBundlePictureSpecs,
    shouldLimitItems,
    variant
  } = props;

  const bundlePictureSpecs =
    VARIANT_PICTURE_SPECS[variant] || baseBundlePictureSpecs;
  const categoryFallback = SITE_NAME;

  // jump through a few hoops to limit the number of content items display while
  // accounting for ads. we need the number of content items so we can set the
  // correct CSS class. ads are displayed/rearranged via CSS grid, so their
  // order is less important.
  const { maxContentItems, bundleStyles } = VARIANTS[variant];
  const truncatedContentItems = items
    .filter(item => !NON_CONTENT_TYPES.includes(item.type))
    .slice(0, maxContentItems);
  const nonContentItems = items.filter(item =>
    NON_CONTENT_TYPES.includes(item.type)
  );
  const spreadItems = shouldLimitItems
    ? [...truncatedContentItems, ...nonContentItems]
    : items;

  const bundleClasses = classnames(
    'Spread',
    [`Spread--${variant}`],
    [`Spread--${truncatedContentItems.length}`],
    {
      'Spread--cake': sponsor,
      'Spread--no-desktop-ad':
        ads && ads.enabled && !ads.enabled.includes('desktop')
    }
  );

  // handle "exceptions" for pictureSpecs. certain image indices have different
  // properties. only certain variants have exceptionPatterns.
  // (DEFAULT_BUNDLE_PICTURE_SPECS does not currently define any exceptions, so
  // this effectively applies only to site-specific bundlePictureSpecs).
  //
  // CLEANUP: this really feels like it should be the responsibility of
  // setBundleItemProps(), but then it makes testing createSetBundleItemProps()
  // more complicated.
  const exceptionPictureSpecs = bundlePictureSpecs.exceptionPatterns
    ? extrapolateExceptions(
        bundlePictureSpecs.exceptionPatterns,
        truncatedContentItems.length
      )
    : {};

  // create the method that assigns props to the bundle items. this needs to be
  // a partially-applied function so we can handle different props
  // (basePictureSpecs, exceptionPictureSpecs) for each variant.
  const setBundleItemProps = createSetBundleItemProps({
    pictureSpecs: bundlePictureSpecs.base,
    exceptionPictureSpecs,
    bundleData: props.data,
    categoryFallback,
    bundleIdx: props.idx
  });

  const titleEl =
    isValidElement(title) === false ? (
      <span dangerouslySetInnerHTML={{ __html: title }} />
    ) : (
      <span>{title}</span>
    );

  return (
    <div className={bundleClasses}>
      {(title || sponsor) && (
        <div className="Spread__title">
          {title && (
            <h4>
              {/* CLEANUP: factor out to link component? */}
              {link && link.url ? (
                <a href={link.url} className="Spread__link">
                  {titleEl}
                </a>
              ) : (
                titleEl
              )}
            </h4>
          )}
          {sponsor && (
            <BundleSponsor
              imageUrl={sponsor.imageUrl}
              messaging={sponsor.messaging}
              name={sponsor.name}
              cssHook="Spread"
            />
          )}
        </div>
      )}
      {description && <p className="Spread__description">{description}</p>}

      <div className="Spread__items">
        {spreadItems.map((item, idx) => (
          <div
            key={item.id}
            className={`Spread__item Spread__item--${idx} Spread__item--${item.type}`}
          >
            <BundleItemComponent
              item={item}
              variant={variant}
              idx={idx}
              customPropsMapper={setBundleItemProps}
              totalItems={spreadItems.length}
              gtmAttrs={gtmAttrs}
              components={BUNDLE_COMPONENTS}
            />
          </div>
        ))}
      </div>
      <style jsx>{bundleStyles}</style>
    </div>
  );
}

SpreadBundle.propTypes = {
  idx: number,
  data: shape({
    ads: shape({
      enabled: arrayOf(string),
      targeting: object
    }),
    title: oneOfType([node, string]),
    desc: string,
    link: shape({
      title: string,
      url: string
    }),
    sponsor: shape({
      imageUrl: string,
      messaging: string,
      name: string
    }),
    fallbackCategory: string
  }),
  gtmAttrs: shape({
    // 'data-gtm-*': '<value>'
  }),
  items: arrayOf(
    shape({
      id: string.isRequired,
      type: string.isRequired,
      data: shape({
        // Teaser, etc.
      })
    })
  ).isRequired,

  /**
   * bundlePictureSpecs is a data structure that describes the rendering of a
   * <picture> element's children (<source>, <img>) across an entire collection
   * of items (each of which has an image destined for said <picture>).
   *
   * it contains two keys: 'base' handles the default "basePictureSpecs"
   * (applied to every image), and 'exceptionPatterns' defines one ore more
   * patterns (start index + recurrence) for affecting specific items in the set
   * along with some "exceptionPictureSpecs" that are applied to indices that
   * match the extrapolated pattern.
   *
   * right now, we're only using this prop type on SpreadBundle, though nothing
   * precludes it from being used elsewhere.
   *
   */
  bundlePictureSpecs: shape({
    base: shape({
      fallbackDims: Teaser.propTypes.fallbackDims,
      sourceSets: Teaser.propTypes.sourceSets
    }).isRequired,
    exceptionPatterns: arrayOf(
      shape({
        positionStart: number.isRequired,
        recurrence: number.isRequired,
        fallbackDims: Teaser.propTypes.fallbackDims,
        sourceSets: Teaser.propTypes.sourceSets
      })
    )
  }),
  shouldLimitItems: bool,
  variant: string
};

SpreadBundle.defaultProps = {
  idx: undefined,
  data: {},
  bundlePictureSpecs: DEFAULT_BUNDLE_PICTURE_SPECS,
  gtmAttrs: {},
  shouldLimitItems: true,
  variant: DEFAULT_VARIANT
};

export default SpreadBundle;
